import { t as uiText, jaText, currentUiLanguage } from '../i18n';
import React, { useEffect, useRef, useCallback, useState } from 'react';
import './AgentHomePage.css';
import { workspacePagePath } from '../utils/workspaceNavigation';
import { decodeCsvFile } from '../utils/decodeCsvFile';
import { useWorkspaceSetupState } from '../hooks/useWorkspaceSetupState';
import { useSetupProposal } from '../hooks/useSetupProposal';
import { useAgentChat } from '../hooks/useAgentChat';
import { useAgentChatHistory } from '../hooks/useAgentChatHistory';
import { useImeCompositionGuard } from '../hooks/useImeCompositionGuard';
import type { AgentChatSessionEntry } from '../components/AgentChatSidebarContent';
import type { AgentChatAction } from '../../functions/src/agentAdvisor/agentChatFn';
import {
  canonicalIngestChoice,
  displayIngestWording,
  downloadCsvTemplate,
  ingestChoiceLabel,
  ingestModeFromFlow,
  isPageTextRequest,
  parseCrawlCsvDetailed,
  parsePagesCsv,
  START_NEW_WORK,
  CRAWL_PREVIEW_DISPLAY_LIMIT,
  type ParsedPagesCsv,
} from '../utils/agentIngest';
import {
  CARD_STARTERS,
  buildDay0ActionCards,
  buildDay0Recommendations,
  isEmbedSecurityStarter,
  type Day0PanelKey,
  type Day0SetupFlags,
} from '../features/onboarding/day0Setup';
import { findReadyEmbedToken, registerEmbedSite, removeEmbedSite } from '../features/onboarding/embedSecurity';
import EmbedSecurityPanel from '../features/onboarding/EmbedSecurityPanel';
import { publishIdFromPublicUrl } from '../features/onboarding/embedSnippet';
import DynamicEmbedCodeModal from '../features/widget/DynamicEmbedCodeModal';
import { listPublicApiTokens, type PublicApiToken } from '../api/publicApiTokens';
import AppearancePanel from '../features/onboarding/AppearancePanel';
import ContactPathPanel from '../features/onboarding/ContactPathPanel';
import FloatingPreview from '../features/onboarding/FloatingPreview';
import {
  appearanceDraftsEqual,
  defaultAppearanceDraft,
  loadAppearanceDraft,
  saveAppearanceDraft,
  type AppearanceDraft,
  type AppearanceWriteCaps,
} from '../features/onboarding/appearanceSettings';
import { useWorkspaceEntitlements } from '../features/plans/useWorkspaceEntitlements';
import {
  defaultWidgetLauncherVisual,
  withAppearanceDraft,
  type WidgetLauncherVisual,
} from '../features/onboarding/widgetLauncherVisual';
import { loadWidgetLauncherVisual } from '../features/onboarding/loadWidgetLauncherVisual';
import { markContactPathDecided } from '../features/onboarding/contactPathSettings';
import {
  contactHintsFromUserMessage,
  extractContactHints,
  type ContactPathSeed,
} from '../features/onboarding/contactPathHints';
import { isBareUrlText, isHostSiteIntent } from '../../functions/src/agentAdvisor/hostSiteIntent';
import { renderAgentMarkdown } from '../features/agent/renderAgentMarkdown';

export type HomeTabKey =
  | 'page'
  | 'thread'
  | 'calendar'
  | 'rag'
  | 'file'
  | 'hashtag'
  | 'public'
  | 'suggest'
  | 'oga'
  | 'dashboard'
  | 'token'
  | 'home';

interface AgentHomePageProps {
  workspaceId: string;
  isAdmin: boolean;
  onNavigateTo: (tab: HomeTabKey) => void;
  publishEnabled: boolean;
  publishedPages: number;
  standbyPages: number;
  publicUrl: string;
  /** Session to load (from sidebar history) */
  selectedSession?: AgentChatSessionEntry | null;
  /** Counter incremented when "新規チャット" is clicked in the sidebar */
  newChatRequest?: number;
  /** Called after current conversation is saved to history */
  onSessionSaved?: (entry: AgentChatSessionEntry) => void;
}

function isChatActionRunning(
  action: { running?: boolean; trackingId?: string } | undefined,
  isLastMessage: boolean,
): boolean {
  if (!action?.running) return false;
  if (action.trackingId) return true;
  return isLastMessage;
}

// ---------------------------------------------------------------------------
// Action card starters
// ---------------------------------------------------------------------------

const AGENT_RAIL_WIDTH_KEY = 'agentHome.railWidth';
const AGENT_RAIL_WIDTH_DEFAULT = 400;
const AGENT_RAIL_WIDTH_MIN = 280;
const AGENT_RAIL_WIDTH_MAX = 560;
const AGENT_RAIL_CHAT_MIN = 360;
const AGENT_RAIL_RESIZER_WIDTH = 16;
const AGENT_RAIL_STACK_BREAKPOINT = 900;
/** Matches the sidebar open/close slide (Layout.css margin-left 0.3s ease). */
const AGENT_RAIL_SLIDE_MS = 300;

function clampAgentRailWidth(width: number, containerWidth?: number): number {
  let max = AGENT_RAIL_WIDTH_MAX;
  if (containerWidth && containerWidth > 0) {
    max = Math.min(max, containerWidth - AGENT_RAIL_CHAT_MIN - AGENT_RAIL_RESIZER_WIDTH);
  }
  max = Math.max(AGENT_RAIL_WIDTH_MIN, max);
  return Math.min(max, Math.max(AGENT_RAIL_WIDTH_MIN, Math.round(width)));
}

function readAgentRailWidth(): number {
  try {
    const raw = window.localStorage.getItem(AGENT_RAIL_WIDTH_KEY);
    const n = raw == null ? AGENT_RAIL_WIDTH_DEFAULT : Number(raw);
    if (!Number.isFinite(n)) return AGENT_RAIL_WIDTH_DEFAULT;
    return clampAgentRailWidth(n);
  } catch {
    return AGENT_RAIL_WIDTH_DEFAULT;
  }
}

function writeAgentRailWidth(width: number) {
  try {
    window.localStorage.setItem(AGENT_RAIL_WIDTH_KEY, String(width));
  } catch {
    // プライベートモードなど、保存できない環境では幅の記憶だけ諦める
  }
}

function isDay0PanelKey(key: string): key is Day0PanelKey {
  return key === 'embedSecurity' || key === 'appearance' || key === 'contact';
}

function day0FlagsFromState(state: {
  crawl: { lastCrawledAt: string | null };
  widget: { hasToken?: boolean; appearanceSaved?: boolean };
  onboarding?: { contactPathDecided?: boolean };
} | null): Day0SetupFlags {
  return {
    lastCrawledAt: state?.crawl.lastCrawledAt ?? null,
    embedReady: state?.widget.hasToken ?? false,
    appearanceSaved: state?.widget.appearanceSaved ?? false,
    contactPathDecided: state?.onboarding?.contactPathDecided ?? false,
  };
}

const AgentHomePage: React.FC<AgentHomePageProps> = ({
  workspaceId,
  isAdmin,
  onNavigateTo: _onNavigateTo,
  publishEnabled,
  publishedPages,
  standbyPages,
  publicUrl,
  selectedSession,
  newChatRequest = 0,
  onSessionSaved,
}) => {
  const { state, loading: stateLoading, fetch: fetchState } = useWorkspaceSetupState();
  const { can } = useWorkspaceEntitlements();
  const { proposal, loading: proposalLoading, generate } = useSetupProposal();

  const [initialMessage, setInitialMessage] = useState<string | undefined>(undefined);
  const [embedTokens, setEmbedTokens] = useState<PublicApiToken[]>([]);
  const [embedTokensLoaded, setEmbedTokensLoaded] = useState(false);
  const [embedCodeOpen, setEmbedCodeOpen] = useState(false);
  const [panel, setPanel] = useState<Day0PanelKey | null>(null);
  // Read during an in-flight send, so it must not come from a stale closure.
  const panelRef = useRef<Day0PanelKey | null>(null);
  panelRef.current = panel;
  const [appearanceDraft, setAppearanceDraft] = useState<AppearanceDraft>(() => defaultAppearanceDraft());
  const [appearanceBaseline, setAppearanceBaseline] = useState<AppearanceDraft>(() => defaultAppearanceDraft());
  const [launcherVisual, setLauncherVisual] = useState<WidgetLauncherVisual>(() => defaultWidgetLauncherVisual());
  const [contactSeed, setContactSeed] = useState<ContactPathSeed>({});
  const [embedSiteDraft, setEmbedSiteDraft] = useState('');
  const [panelSaving, setPanelSaving] = useState(false);
  const [embedOriginRemoving, setEmbedOriginRemoving] = useState(false);
  const [widgetPreviewOpen, setWidgetPreviewOpen] = useState(false);
  const [buttonPreviewVisible, setButtonPreviewVisible] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const homeRef = useRef<HTMLDivElement>(null);
  const [railWidth, setRailWidth] = useState(readAgentRailWidth);
  const [railResizing, setRailResizing] = useState(false);
  const [railAnimating, setRailAnimating] = useState(false);
  const railWidthRef = useRef(railWidth);
  const railPreferredRef = useRef(railWidth);
  // Width to put back when the setup accordion closes. Null while it is closed.
  const railRestoreRef = useRef<number | null>(null);
  const railDragOriginRef = useRef<{ x: number; width: number } | null>(null);
  const railAnimateTimerRef = useRef<number | null>(null);
  const railRef = useRef<HTMLElement>(null);
  const pendingPagesCsvRef = useRef<ParsedPagesCsv | null>(null);
  const [cardTitle, setCardTitle] = useState('');
  const [cardBody, setCardBody] = useState('');

  const handleFrontendAction = useCallback((action: AgentChatAction) => {
    if (action.type === 'openAppearancePanel') {
      setPanel('appearance');
      return;
    }
    if (action.type === 'openContactPanel') {
      setPanel('contact');
      const hints = extractContactHints(
        [action.params?.phoneNumber, action.params?.inquiryUrl, action.params?.phone, action.params?.url]
          .filter((v) => typeof v === 'string')
          .join(' ')
      );
      if (hints.phoneNumber || hints.inquiryUrl) {
        setContactSeed((prev) => ({ ...prev, ...hints }));
      }
      return;
    }
    if (action.type === 'openEmbedSecurityPanel') {
      setPanel('embedSecurity');
      const siteUrl = typeof action.params?.siteUrl === 'string' ? action.params.siteUrl.trim() : '';
      if (siteUrl) setEmbedSiteDraft(siteUrl);
      return;
    }
    if (action.type !== 'offerCsvTemplate') return;
    const kind = action.params?.kind === 'pages' ? 'pages' : 'crawl';
    downloadCsvTemplate(kind);
  }, []);

  const getNextWorkRecommendations = useCallback(() => {
    return buildDay0Recommendations(day0FlagsFromState(state), proposal?.messageKey);
  }, [state, proposal?.messageKey]);

  const {
    messages,
    sending,
    send,
    clearMessages,
    restoreMessages,
    prefillRef,
    markChoicesUsed,
    getSessionId,
    ingestFlow,
    applyIngestEvent,
    createPageFromText,
  } = useAgentChat({
    workspaceId,
    initialMessage,
    onFrontendAction: handleFrontendAction,
    getNextWorkRecommendations,
    getOpenPanel: () => panelRef.current,
    onBackgroundJobFinished: () => {
      if (workspaceId) void fetchState(workspaceId);
    },
    getPendingPagesCsv: () => pendingPagesCsvRef.current,
    clearPendingPagesCsv: () => {
      pendingPagesCsvRef.current = null;
    },
    onSessionPersisted: (sessionId) => {
      onSessionSaved?.({
        sessionId,
        title: null,
        updatedAt: new Date(),
        messages: [],
      });
    },
  });

  const ingestMode = ingestModeFromFlow(ingestFlow);
  const showPageCreateCard = ingestMode === 'page-text' && ingestFlow.step !== 'done';
  const ingestActive = !!ingestFlow.path && ingestFlow.step !== 'done';

  useEffect(() => {
    if (!showPageCreateCard) {
      setCardTitle('');
      setCardBody('');
    }
  }, [showPageCreateCard]);

  const history = useAgentChatHistory();
  const discardEmptySessions = history.discardEmptySessions;

  useEffect(() => {
    if (!workspaceId) return;
    void discardEmptySessions(workspaceId);
  }, [workspaceId, discardEmptySessions]);

  const [inputText, setInputText] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);
  const atBottomRef = useRef(true);
  const [atBottom, setAtBottom] = useState(true);
  const { onCompositionStart, onCompositionEnd, shouldBlockEnterSubmit } =
    useImeCompositionGuard();

  // Track previous newChatRequest to detect changes
  const prevNewChatRequestRef = useRef(newChatRequest);

  // Wire prefillRef
  const prefillInput = useCallback((text: string) => {
    setInputText(text);
    inputRef.current?.focus();
  }, []);
  useEffect(() => {
    prefillRef.current = prefillInput;
  }, [prefillRef, prefillInput]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
  }, []);

  // 末尾付近にいるときだけ追従する（過去を読んでいる最中は動かさない）
  const handleMessagesScroll = useCallback(() => {
    const el = messagesRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    const next = distance < 80;
    atBottomRef.current = next;
    setAtBottom((prev) => (prev === next ? prev : next));
  }, []);

  const jumpToLatest = useCallback(() => {
    atBottomRef.current = true;
    setAtBottom(true);
    scrollToBottom();
  }, [scrollToBottom]);

  // Auto-scroll
  useEffect(() => {
    if (!atBottomRef.current) return;
    scrollToBottom();
  }, [messages, sending, scrollToBottom]);

  // Fetch setup state
  useEffect(() => {
    if (!workspaceId || !isAdmin) return;
    fetchState(workspaceId);
  }, [workspaceId, isAdmin, fetchState]);

  useEffect(() => {
    if (!workspaceId || !isAdmin) return;
    let cancelled = false;
    Promise.all([loadAppearanceDraft(workspaceId), loadWidgetLauncherVisual(workspaceId)])
      .then(([draft, visual]) => {
        if (!cancelled) {
          setAppearanceDraft(draft);
          setAppearanceBaseline({ ...draft });
          setLauncherVisual(visual);
        }
      })
      .catch((err) => {
        console.warn('[AgentHomePage] loadAppearanceDraft failed', err);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, isAdmin, state?.widget.appearanceSaved]);

  useEffect(() => {
    if (!workspaceId || !isAdmin) return;
    let cancelled = false;
    setEmbedTokensLoaded(false);
    listPublicApiTokens({ workspaceId })
      .then((res) => {
        if (cancelled) return;
        setEmbedTokens(res.tokens);
      })
      .catch((err) => {
        console.warn('[AgentHomePage] listPublicApiTokens failed', err);
        if (!cancelled) setEmbedTokens([]);
      })
      .finally(() => {
        if (!cancelled) setEmbedTokensLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, isAdmin, state?.widget.hasToken]);

  // Generate proposal
  useEffect(() => {
    if (!workspaceId || !isAdmin || !state || initialMessage !== undefined) return;
    generate({
      workspaceId,
      language: currentUiLanguage(),
      publishEnabled,
      publishedPages,
      standbyPages,
      publicUrl: publicUrl || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, isAdmin, state]);

  useEffect(() => {
    if (proposal?.proposalText && initialMessage === undefined) {
      setInitialMessage(proposal.proposalText);
    }
  }, [proposal?.proposalText, initialMessage]);

  // ---- Sidebar: load selected session ----
  useEffect(() => {
    if (!selectedSession) return;
    if (selectedSession.sessionId === getSessionId()) return;
    pendingPagesCsvRef.current = null;
    restoreMessages(
      selectedSession.messages,
      selectedSession.sessionId,
      selectedSession.ingestFlow
    );
    void discardEmptySessions(workspaceId, { keepId: selectedSession.sessionId });
  }, [selectedSession, restoreMessages, getSessionId, discardEmptySessions, workspaceId]);

  // ---- Sidebar: new chat request ----
  useEffect(() => {
    if (newChatRequest === prevNewChatRequestRef.current) return;
    prevNewChatRequestRef.current = newChatRequest;

    const startDraft = async () => {
      const existingSessionId = getSessionId();
      const hasUser = messages.some((m) => m.role === 'user');
      // persistSession already wrote this conversation; don't addDoc a duplicate.
      if (hasUser && !existingSessionId) {
        await history.saveSession(messages, workspaceId, ingestFlow);
      }
      await history.discardEmptySessions(workspaceId);
      clearMessages();
      setInitialMessage(undefined);
      pendingPagesCsvRef.current = null;
      // A panel left open keeps the previous conversation's draft next to its save
      // button, so the new chat could save a value it never asked about.
      setPanel(null);
      setContactSeed({});
      setEmbedSiteDraft('');
      setCardTitle('');
      setCardBody('');
      if (state) {
        generate({
          workspaceId,
          language: currentUiLanguage(),
          publishEnabled,
          publishedPages,
          standbyPages,
          publicUrl: publicUrl || null,
        });
      }
    };
    void startDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newChatRequest]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleSend = useCallback(async () => {
    if (!inputText.trim() || sending) return;
    const text = inputText.trim();
    const hostSiteTurn =
      isEmbedSecurityStarter(text) ||
      isHostSiteIntent(text) ||
      (panelRef.current === 'embedSecurity' && isBareUrlText(text));
    if (hostSiteTurn) {
      // A 掲載サイト URL is a host origin, not an inquiry link. Keep the contact
      // panel out of it; the server opens the host-site panel for this turn.
      setPanel('embedSecurity');
    } else if (!ingestActive) {
      if (isPageTextRequest(text)) {
        // The model will describe the create card whether or not it is on screen,
        // so the flow has to be moved here rather than left to the reply.
        applyIngestEvent({ type: 'choice', label: jaText('Add pages to notreu') });
        applyIngestEvent({ type: 'choice', label: jaText('Add one item as text') });
      } else {
        // Inside the ingest wizard a pasted URL is a crawl target, so it must not
        // be drafted as the inquiry link.
        const hints = contactHintsFromUserMessage(text);
        if (hints.phoneNumber || hints.inquiryUrl) {
          setPanel('contact');
          setContactSeed((prev) => ({
            ...prev,
            ...hints,
          }));
        }
      }
    }
    setInputText('');
    atBottomRef.current = true;
    setAtBottom(true);
    await send(text);
  }, [inputText, sending, send, ingestActive, applyIngestEvent]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey && !shouldBlockEnterSubmit(e)) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend, shouldBlockEnterSubmit]
  );

  const handleChoiceClick = useCallback(
    async (choice: string, messageId: string) => {
      markChoicesUsed(messageId);
      setInputText('');
      if (choice === START_NEW_WORK) {
        const latest = workspaceId ? await fetchState(workspaceId) : null;
        await send(choice, {
          localReply: buildDay0Recommendations(day0FlagsFromState(latest), proposal?.messageKey),
        });
        return;
      }
      if (isEmbedSecurityStarter(choice)) {
        setPanel('embedSecurity');
        await send(choice, {
          skipAgent: true,
          localReply: { content: uiText("Enter the host site URL in the panel on the right.") },
        });
        return;
      }
      if (choice === jaText("Start over")) {
        pendingPagesCsvRef.current = null;
        setCardTitle('');
        setCardBody('');
        applyIngestEvent({ type: 'redo' });
      } else {
        applyIngestEvent({ type: 'choice', label: canonicalIngestChoice(choice) });
      }
      await send(canonicalIngestChoice(choice));
    },
    [send, markChoicesUsed, applyIngestEvent, fetchState, workspaceId]
  );

  const embedPublishId = publishIdFromPublicUrl(publicUrl);
  const readyEmbedToken = findReadyEmbedToken(embedTokens);
  const embedReady = readyEmbedToken !== null;

  const handleCardClick = useCallback(
    (key: string) => {
      if (isDay0PanelKey(key)) {
        setPanel((current) => (current === key ? null : key));
        return;
      }
      prefillInput(CARD_STARTERS[key] ?? key);
    },
    [prefillInput]
  );

  const handleRegisterEmbedSite = useCallback(async (siteUrl: string) => {
    if (!workspaceId || panelSaving || embedOriginRemoving) return;
    setPanelSaving(true);
    try {
      await registerEmbedSite(workspaceId, siteUrl, embedTokens);
      const listed = await listPublicApiTokens({ workspaceId });
      setEmbedTokens(listed.tokens);
      await fetchState(workspaceId);
      setEmbedSiteDraft('');
      await send(CARD_STARTERS.embedSecurity, {
        skipAgent: true,
        localReply: { content: uiText("Registered the host site and issued a token. The embed script is shown in the status.") },
      });
    } catch (err: any) {
      alert(uiText("Failed to register the host site: ") + (err?.message ?? ''));
    } finally {
      setPanelSaving(false);
    }
  }, [embedOriginRemoving, embedTokens, fetchState, panelSaving, send, workspaceId]);

  const handleRemoveEmbedSite = useCallback(async (origin: string) => {
    if (!workspaceId || panelSaving || embedOriginRemoving) return;
    if (!confirm(uiText("Delete origin \"{{origin}}\"?", { origin }))) return;
    setEmbedOriginRemoving(true);
    try {
      await removeEmbedSite(origin, embedTokens);
      const listed = await listPublicApiTokens({ workspaceId });
      setEmbedTokens(listed.tokens);
      await fetchState(workspaceId);
    } catch {
      alert(uiText("Failed to delete the origin"));
    } finally {
      setEmbedOriginRemoving(false);
    }
  }, [embedOriginRemoving, embedTokens, fetchState, panelSaving, workspaceId]);

  const handleSaveAppearance = useCallback(async () => {
    if (!workspaceId || panelSaving) return;
    setPanelSaving(true);
    try {
      const caps: AppearanceWriteCaps = {
        buttonImage: can('widget.buttonImage'),
        icon: can('widget.icon'),
        tooltip: can('widget.tooltip'),
        designColor: can('publicRag.designColor'),
        contentText: can('publicRag.contentText'),
      };
      await saveAppearanceDraft(workspaceId, appearanceDraft, caps);
      setLauncherVisual((current) => withAppearanceDraft(current, appearanceDraft, appearanceBaseline));
      setAppearanceBaseline({ ...appearanceDraft });
      await fetchState(workspaceId);
      setPanel(null);
      await send(CARD_STARTERS.appearance, {
        skipAgent: true,
        localReply: { content: uiText("Appearance saved. Next, set the contact path.") },
      });
    } catch (err: any) {
      alert(uiText("Failed to save appearance: ") + (err?.message ?? ''));
    } finally {
      setPanelSaving(false);
    }
  }, [appearanceBaseline, appearanceDraft, can, fetchState, panelSaving, send, workspaceId]);

  const handleContactSaved = useCallback(async () => {
    if (!workspaceId) return;
    await markContactPathDecided(workspaceId);
    await fetchState(workspaceId);
    setPanel(null);
    await send(CARD_STARTERS.contact, {
      skipAgent: true,
      localReply: { content: uiText("Contact path saved. Initial setup is complete.") },
    });
  }, [fetchState, send, workspaceId]);

  const handleCreatePage = useCallback(async () => {
    if (!cardBody.trim() || sending) return;
    const ok = await createPageFromText({ title: cardTitle, content: cardBody });
    if (ok) {
      setCardTitle('');
      setCardBody('');
      if (workspaceId) void fetchState(workspaceId);
    }
  }, [cardBody, cardTitle, sending, createPageFromText, workspaceId, fetchState]);

  const handleCancelPageCreate = useCallback(() => {
    if (sending) return;
    setCardTitle('');
    setCardBody('');
    applyIngestEvent({ type: 'reset' });
  }, [sending, applyIngestEvent]);

  const ingestCsvFile = useCallback(
    async (file: File) => {
      const text = await decodeCsvFile(file);
      const path = ingestFlow.path;

      const sendPagesPreview = async () => {
        const parsed = parsePagesCsv(text);
        if (parsed.rowCount === 0) {
          alert(uiText("No valid page rows were found. Put the title in the first column and the body in the second."));
          return;
        }
        pendingPagesCsvRef.current = parsed;
        const preview = parsed.previews
          .slice(0, 8)
          .map((p) => (p.snippet ? `${p.title}（${p.snippet}）` : p.title))
          .join('、');
        await send(
          uiText("Detected {{rowCount}} page items in CSV \"{{name}}\". Example titles: {{preview}}{{v3}}. Please review the list.", { name: file.name, rowCount: parsed.rowCount, preview: preview, v3: parsed.previews.length > 8 ? '…' : '' }),
          {
            pagePreviewList: parsed.previews
              .slice(0, 20)
              .map((p) => (p.snippet ? `${p.title} — ${p.snippet}` : p.title)),
            skipUrlExtract: true,
          }
        );
      };

      const sendCrawlPreview = async () => {
        const parsed = parseCrawlCsvDetailed(text);
        if (parsed.urls.length === 0) {
          alert(uiText("No valid URL was found. Put a URL in the first column. The first row can be a URL."));
          return;
        }
        pendingPagesCsvRef.current = null;

        // スキップ理由の内訳を生成
        const skipParts: string[] = [];
        if (parsed.duplicateCount > 0) skipParts.push(uiText("Duplicates {{duplicateCount}} items", { duplicateCount: parsed.duplicateCount }));
        if (parsed.placeholderCount > 0) skipParts.push(uiText("Placeholders {{placeholderCount}} items", { placeholderCount: parsed.placeholderCount }));
        if (parsed.invalidCount > 0) skipParts.push(uiText("{{invalidCount}} invalid URLs", { invalidCount: parsed.invalidCount }));
        const skippedTotal = parsed.rawCount - parsed.urls.length;
        const skipNote = skippedTotal > 0
          ? uiText("({{skippedTotal}} items excluded: {{v1}})", { skippedTotal: skippedTotal, v1: skipParts.join('、') })
          : '';

        await send(
          uiText("Detected {{length}} URL items in CSV \"{{name}}\"{{skipNote}}. Please confirm whether to register them.", { name: file.name, length: parsed.urls.length, skipNote: skipNote }),
          { extractedUrls: parsed.urls }
        );
      };

      if (path === 'pages') {
        await sendPagesPreview();
        return;
      }
      if (path === 'crawl') {
        await sendCrawlPreview();
        return;
      }

      const pagesAuto = parsePagesCsv(text);
      const parsedAuto = parseCrawlCsvDetailed(text);
      // The pages header alone does not decide it: a URL list exported with a
      // Title/Content header still has to go down the crawl branch.
      if (pagesAuto.hasHeader && parsedAuto.urls.length === 0) {
        // An empty or unreadable CSV must not leave the wizard in a pages-CSV
        // state the admin never chose, so commit the path only once it parsed.
        if (pagesAuto.rowCount > 0) {
          applyIngestEvent({ type: 'offerCsvTemplate', kind: 'pages' });
        }
        await sendPagesPreview();
        return;
      }
      if (parsedAuto.urls.length === 0) {
        await send(
          uiText("Received CSV \"{{name}}\". Is this a list of URLs for crawling a site, or pages (title and body) to add to notreu?", { name: file.name })
        );
        return;
      }
      applyIngestEvent({ type: 'offerCsvTemplate', kind: 'crawl' });
      pendingPagesCsvRef.current = null;
      const autoSkipParts: string[] = [];
      if (parsedAuto.duplicateCount > 0) autoSkipParts.push(uiText("Duplicates {{duplicateCount}} items", { duplicateCount: parsedAuto.duplicateCount }));
      if (parsedAuto.placeholderCount > 0) autoSkipParts.push(uiText("Placeholders {{placeholderCount}} items", { placeholderCount: parsedAuto.placeholderCount }));
      if (parsedAuto.invalidCount > 0) autoSkipParts.push(uiText("{{invalidCount}} invalid URLs", { invalidCount: parsedAuto.invalidCount }));
      const autoSkipTotal = parsedAuto.rawCount - parsedAuto.urls.length;
      const autoSkipNote = autoSkipTotal > 0
        ? uiText("({{autoSkipTotal}} items excluded: {{v1}})", { autoSkipTotal: autoSkipTotal, v1: autoSkipParts.join('、') })
        : '';
      await send(
        uiText("Detected {{length}} URL items in CSV \"{{name}}\"{{autoSkipNote}}. Please confirm whether to register them.", { name: file.name, length: parsedAuto.urls.length, autoSkipNote: autoSkipNote }),
        { extractedUrls: parsedAuto.urls }
      );
    },
    [ingestFlow.path, send, applyIngestEvent]
  );

  const handleCsvFile = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        await ingestCsvFile(file);
      } catch (err: any) {
        alert(uiText("Failed to load the CSV: ") + (err?.message ?? ''));
      } finally {
        if (csvInputRef.current) csvInputRef.current.value = '';
      }
    },
    [ingestCsvFile]
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (sending) return;
      const file = Array.from(e.dataTransfer.files).find(
        (f) => f.name.toLowerCase().endsWith('.csv') || f.type === 'text/csv'
      );
      if (!file) return;
      try {
        await ingestCsvFile(file);
      } catch (err: any) {
        alert(uiText("Failed to load the CSV: ") + (err?.message ?? ''));
      }
    },
    [ingestCsvFile, sending]
  );

  const cancelRailSlide = useCallback(() => {
    if (railAnimateTimerRef.current == null) return;
    window.clearTimeout(railAnimateTimerRef.current);
    railAnimateTimerRef.current = null;
    const visual = railRef.current?.getBoundingClientRect().width;
    if (visual && Number.isFinite(visual)) {
      const width = Math.round(visual);
      railWidthRef.current = width;
      setRailWidth(width);
    }
    setRailAnimating(false);
  }, []);

  const slideRailTo = useCallback((width: number) => {
    railWidthRef.current = width;
    setRailWidth(width);
    setRailAnimating(true);
    if (railAnimateTimerRef.current != null) window.clearTimeout(railAnimateTimerRef.current);
    railAnimateTimerRef.current = window.setTimeout(() => {
      railAnimateTimerRef.current = null;
      setRailAnimating(false);
    }, AGENT_RAIL_SLIDE_MS);
  }, []);

  useEffect(() => () => {
    if (railAnimateTimerRef.current != null) window.clearTimeout(railAnimateTimerRef.current);
  }, []);

  const applyRailWidth = useCallback((requested: number, persist: boolean) => {
    cancelRailSlide();
    const applied = clampAgentRailWidth(requested, homeRef.current?.clientWidth);
    railWidthRef.current = applied;
    setRailWidth(applied);
    // An open setup accordion forces the rail to its max. Don't replace the
    // width the user had before opening it.
    if (panelRef.current && window.innerWidth > AGENT_RAIL_STACK_BREAKPOINT) return applied;
    railPreferredRef.current = applied;
    if (persist) writeAgentRailWidth(applied);
    return applied;
  }, [cancelRailSlide]);

  useEffect(() => {
    const fit = () => {
      if (window.innerWidth <= AGENT_RAIL_STACK_BREAKPOINT) return;
      cancelRailSlide();
      if (panelRef.current) {
        if (railRestoreRef.current == null) {
          railRestoreRef.current = railPreferredRef.current;
        }
        const applied = clampAgentRailWidth(AGENT_RAIL_WIDTH_MAX, homeRef.current?.clientWidth);
        railWidthRef.current = applied;
        setRailWidth(applied);
        return;
      }
      const applied = clampAgentRailWidth(railPreferredRef.current, homeRef.current?.clientWidth);
      railWidthRef.current = applied;
      setRailWidth(applied);
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [cancelRailSlide]);

  useEffect(() => {
    const stacked = window.innerWidth <= AGENT_RAIL_STACK_BREAKPOINT;
    if (panel) {
      if (stacked) return;
      if (railRestoreRef.current == null) {
        railRestoreRef.current = railPreferredRef.current;
      }
      slideRailTo(clampAgentRailWidth(AGENT_RAIL_WIDTH_MAX, homeRef.current?.clientWidth));
      return;
    }
    if (railRestoreRef.current == null) return;
    const restore = railRestoreRef.current;
    railRestoreRef.current = null;
    railPreferredRef.current = restore;
    if (stacked) return;
    slideRailTo(clampAgentRailWidth(restore, homeRef.current?.clientWidth));
  }, [panel, slideRailTo]);

  const onRailResizePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    const visual = railRef.current?.getBoundingClientRect().width;
    const width = visual && Number.isFinite(visual) ? Math.round(visual) : railWidthRef.current;
    cancelRailSlide();
    railWidthRef.current = width;
    setRailWidth(width);
    railDragOriginRef.current = { x: e.clientX, width };
    setRailResizing(true);
  }, [cancelRailSlide]);

  const onRailResizePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const origin = railDragOriginRef.current;
    if (!origin || !e.currentTarget.hasPointerCapture(e.pointerId)) return;
    applyRailWidth(origin.width + (origin.x - e.clientX), false);
  }, [applyRailWidth]);

  const onRailResizePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!railDragOriginRef.current) return;
    railDragOriginRef.current = null;
    setRailResizing(false);
    if (!panelRef.current) writeAgentRailWidth(railPreferredRef.current);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }, []);

  const onRailResizeKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 48 : 16;
    const current = railWidthRef.current;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      applyRailWidth(current + step, true);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      applyRailWidth(current - step, true);
    } else if (e.key === 'Home') {
      e.preventDefault();
      applyRailWidth(AGENT_RAIL_WIDTH_MIN, true);
    } else if (e.key === 'End') {
      e.preventDefault();
      applyRailWidth(AGENT_RAIL_WIDTH_MAX, true);
    }
  }, [applyRailWidth]);

  if (!isAdmin) return null;

  const actionCards = state
    ? buildDay0ActionCards({ ...day0FlagsFromState(state), embedReady })
    : [];

  // まだ会話が始まっていない間は入力欄を画面中央に置く
  const isFreshChat = !messages.some((m) => m.role === 'user');

  return (
    <div
      ref={homeRef}
      className={`agent-home${railResizing ? ' agent-home--resizing' : ''}${railAnimating ? ' agent-home--rail-animate' : ''}`}
      style={{ '--agent-rail-width': `${railWidth}px` } as React.CSSProperties}
    >
      {/* チャット */}
      <section
        className={`agent-home__chat${isFreshChat ? ' agent-home__chat--fresh' : ''}${isDragging ? ' agent-home__chat--drop' : ''}${ingestMode === 'crawl-csv' || ingestMode === 'pages-csv' ? ' agent-home__chat--csv-ready' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={(e) => {
          if (e.currentTarget.contains(e.relatedTarget as Node)) return;
          setIsDragging(false);
        }}
        onDrop={handleDrop}
      >
        {/* チャットヘッダー */}
        <div className="agent-home__chat-header">
          <span className="agent-home__chat-header-title">{uiText("✨ Agent")}</span>
          {(ingestMode === 'crawl-csv' || ingestMode === 'pages-csv') && (
            <span className="agent-home__chat-header-hint">
              {uiText("Drop a CSV here / select from 📎")}
              {ingestMode === 'crawl-csv' ? uiText("(The first row can be a URL)") : uiText("(The first row can be a header or data)")}
            </span>
          )}
        </div>
        {isDragging && (
          <div className="agent-home__drop-overlay" aria-hidden>{uiText("Drop a CSV to import")}</div>
        )}

        {/* メッセージリスト */}
        <div
          className="agent-home__chat-messages"
          ref={messagesRef}
          onScroll={handleMessagesScroll}
        >
          {(proposalLoading || stateLoading) && messages.length === 0 && (
            <div className="agent-home__chat-skeleton">
              <div className="agent-home__chat-skeleton-line" />
              <div className="agent-home__chat-skeleton-line agent-home__chat-skeleton-line--short" />
            </div>
          )}
          {messages.map((msg, index) => {
            const actionRunning = isChatActionRunning(
              msg.executedAction,
              index === messages.length - 1
            );
            return (
            <div key={msg.id} className="agent-home__chat-message-group">
              <div
                className={`agent-home__chat-bubble agent-home__chat-bubble--${msg.role}`}
              >
                {msg.role === 'assistant' && (
                  <span className="agent-home__chat-avatar">✨</span>
                )}
                <div className="agent-home__chat-bubble-body">
                  {msg.role === 'assistant' ? (
                    <div className="agent-home__chat-text agent-home__chat-text--md">
                      {renderAgentMarkdown(displayIngestWording(msg.content))}
                    </div>
                  ) : (
                    <p className="agent-home__chat-text">{displayIngestWording(msg.content)}</p>
                  )}
                  {msg.truncatedByLimit && (
                    <p className="agent-home__chat-truncated">{uiText("This turn ended because the limit was reached")}</p>
                  )}
                  {msg.executedAction && (
                    <div
                      className={`agent-home__chat-action-result${
                        actionRunning
                          ? ' agent-home__chat-action-result--running'
                          : msg.executedAction.success
                          ? ''
                          : ' agent-home__chat-action-result--error'
                      }`}
                    >
                      {actionRunning ? (
                        <span className="agent-home__chat-progress">
                          <span className="agent-home__chat-progress-spinner" />
                          {msg.executedAction.label}…
                        </span>
                      ) : msg.executedAction.success ? (
                        `✓ ${msg.executedAction.label}${msg.executedAction.resultText ? ` (${msg.executedAction.resultText})` : ''}`
                      ) : (
                        `✗ ${msg.executedAction.label}${msg.executedAction.resultText ? `: ${msg.executedAction.resultText}` : ''}`
                      )}
                    </div>
                  )}
                  {msg.createdPage?.slug && (
                    <a
                      className="agent-home__chat-page-link"
                      href={workspacePagePath(workspaceId, msg.createdPage.slug)}
                    >
                      {uiText("Open page")}
                    </a>
                  )}
                  {/* Inline crawl target list */}
                  {msg.crawlTargetList && msg.crawlTargetList.length > 0 && (
                    <ul className="agent-home__chat-target-list">
                      {msg.crawlTargetList.slice(0, CRAWL_PREVIEW_DISPLAY_LIMIT).map((t) => (
                        <li key={t.url} className="agent-home__chat-target-item">
                          <span className={`agent-home__chat-target-status agent-home__chat-target-status--${t.status}`} />
                          <a href={t.url} target="_blank" rel="noopener noreferrer" className="agent-home__chat-target-url">
                            {t.url}
                          </a>
                          <span className="agent-home__chat-target-badge">
                            {t.status === 'preview' || t.status === 'new' ? uiText("Not registered") : t.status === 'exists' ? uiText("Already registered") : t.status}
                          </span>
                        </li>
                      ))}
                      {msg.crawlTargetList.length > CRAWL_PREVIEW_DISPLAY_LIMIT && (
                        <li className="agent-home__chat-target-more">
                          {uiText("Others")} {msg.crawlTargetList.length - CRAWL_PREVIEW_DISPLAY_LIMIT} {uiText(" items")}
                        </li>
                      )}
                    </ul>
                  )}
                  {msg.pagePreviewList && msg.pagePreviewList.length > 0 && (
                    <ul className="agent-home__chat-target-list">
                      {msg.pagePreviewList.map((title, idx) => (
                        <li key={`${title}-${idx}`} className="agent-home__chat-target-item">
                          <span className="agent-home__chat-target-status agent-home__chat-target-status--preview" />
                          <span className="agent-home__chat-target-url">{title}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              {/* Quick-reply choices */}
              {msg.choices && msg.choices.length > 0 && !msg.choicesUsed && (
                <div className="agent-home__chat-choices">
                  {msg.choices.map((choice) => (
                    <button
                      key={choice}
                      className="agent-home__chat-choice-btn"
                      onClick={() => handleChoiceClick(choice, msg.id)}
                      disabled={sending}
                    >
                      {ingestChoiceLabel(choice)}
                    </button>
                  ))}
                </div>
              )}
            </div>
            );
          })}
          {sending && (
            <div className="agent-home__chat-bubble agent-home__chat-bubble--assistant">
              <span className="agent-home__chat-avatar">✨</span>
              <div className="agent-home__chat-bubble-body">
                <div className="agent-home__chat-typing">
                  <span /><span /><span />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {!isFreshChat && !atBottom && (
          <div className="agent-home__chat-jump-wrap">
            <button
              type="button"
              className="agent-home__chat-jump"
              onClick={jumpToLatest}
              aria-label={uiText("Jump to the latest message")}
              title={uiText("Jump to latest")}
            >
              ↓
            </button>
          </div>
        )}

        {showPageCreateCard && (
          <div className="agent-home__page-card">
            <div className="agent-home__page-card-field">
              <label className="agent-home__page-card-label" htmlFor="agent-page-card-title">
                {uiText("Title (optional)")}
              </label>
              <input
                id="agent-page-card-title"
                className="agent-home__page-card-title"
                type="text"
                value={cardTitle}
                onChange={(e) => setCardTitle(e.target.value)}
                disabled={sending}
              />
            </div>
            <textarea
              className="agent-home__page-card-body"
              value={cardBody}
              onChange={(e) => setCardBody(e.target.value)}
              placeholder={uiText("Page body")}
              rows={5}
              disabled={sending}
            />
            <div className="agent-home__page-card-actions">
              <button
                type="button"
                className="agent-home__page-card-cancel"
                onClick={handleCancelPageCreate}
                disabled={sending}
              >
                {uiText("Cancel")}
              </button>
              <button
                type="button"
                className="agent-home__page-card-submit"
                onClick={() => { void handleCreatePage(); }}
                disabled={!cardBody.trim() || sending}
              >
                {uiText("Create with this content")}
              </button>
            </div>
          </div>
        )}

        {/* 入力欄 */}
        <div className="agent-home__chat-input-row">
          {/* CSV アップロードボタン */}
          <button
            className="agent-home__chat-csv-btn"
            title={
              ingestMode === 'pages-csv'
                ? uiText("Upload page CSV")
                : ingestMode === 'crawl-csv'
                ? uiText("Upload a CSV of crawl URLs")
                : uiText("Upload CSV")
            }
            onClick={() => csvInputRef.current?.click()}
            disabled={sending}
          >
            📎
          </button>
          <input
            ref={csvInputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={handleCsvFile}
          />
          <textarea
            ref={inputRef}
            className="agent-home__chat-input"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            onCompositionStart={onCompositionStart}
            onCompositionEnd={onCompositionEnd}
            placeholder={
              ingestMode === 'crawl-text'
                ? uiText("Paste a URL (press Enter to send)")
                : ingestMode === 'page-text'
                ? uiText("Fill in the card or send a message")
                : ingestMode === 'crawl-csv' || ingestMode === 'pages-csv'
                ? uiText("Drop a CSV, or select from 📎 (press Enter to send)")
                : uiText("Ask anything (Enter to send, Shift+Enter for a new line)")
            }
            rows={1}
            disabled={sending}
          />
          <button
            className="agent-home__chat-send"
            onClick={handleSend}
            disabled={!inputText.trim() || sending}
            aria-label={uiText("Send")}
          >
            ↑
          </button>
        </div>
        <FloatingPreview
          visual={withAppearanceDraft(launcherVisual, appearanceDraft, appearanceBaseline)}
          open={widgetPreviewOpen}
          onToggle={() => setWidgetPreviewOpen((v) => !v)}
          publishId={embedPublishId}
          apiToken={readyEmbedToken?.token}
          buttonVisible={buttonPreviewVisible}
          onHideButton={() => setButtonPreviewVisible(false)}
        />
      </section>

      <div
        className="agent-home__rail-resizer"
        role="separator"
        aria-orientation="vertical"
        aria-label={uiText("Resize")}
        aria-valuemin={AGENT_RAIL_WIDTH_MIN}
        aria-valuemax={AGENT_RAIL_WIDTH_MAX}
        aria-valuenow={railWidth}
        tabIndex={0}
        onPointerDown={onRailResizePointerDown}
        onPointerMove={onRailResizePointerMove}
        onPointerUp={onRailResizePointerUp}
        onPointerCancel={onRailResizePointerUp}
        onKeyDown={onRailResizeKeyDown}
      />

      <aside ref={railRef} className="agent-home__rail">
        <section className="agent-home__preview-toggle">
          <p className="agent-home__preview-toggle-label">{uiText("Button preview")}</p>
          <div className="agent-home__preview-toggle-control">
            <span
              className={`agent-home__preview-toggle-state${buttonPreviewVisible ? ' agent-home__preview-toggle-state--on' : ' agent-home__preview-toggle-state--off'}`}
            >
              {buttonPreviewVisible ? 'ON' : 'OFF'}
            </span>
            <button
              type="button"
              className="agent-home__preview-toggle-switch"
              role="switch"
              aria-checked={buttonPreviewVisible}
              aria-label={uiText("Button preview")}
              onClick={() => setButtonPreviewVisible((visible) => !visible)}
            >
              <span
                className="agent-home__preview-toggle-knob"
                style={{ left: buttonPreviewVisible ? 26 : 2 }}
              />
            </button>
          </div>
        </section>
        {panel == null && (
        <section className="agent-home__summary">
          <p className="agent-home__rail-label">{uiText("Public data")}</p>
          <dl className="agent-home__status-list">
            <div className="agent-home__status-row">
              <dt>{uiText("Public button script")}</dt>
              <dd>
                {stateLoading || (isAdmin && !embedTokensLoaded) ? '…' : embedReady ? (
                  <button
                    type="button"
                    className="agent-home__status-action"
                    onClick={() => {
                      if (readyEmbedToken && embedPublishId) setEmbedCodeOpen(true);
                    }}
                  >
                    {uiText("Copy script")}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="agent-home__status-action"
                    onClick={() => setPanel('embedSecurity')}
                  >
                    {uiText("Register site")}
                  </button>
                )}
              </dd>
            </div>
            <div className="agent-home__status-row">
              <dt>{uiText("Public link")}</dt>
              <dd>
                {stateLoading ? '…' : publicUrl ? (
                  <a
                    className="agent-home__status-link"
                    href={publicUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {publicUrl}
                  </a>
                ) : uiText("Not set")}
              </dd>
            </div>
          </dl>
        </section>
        )}
        {embedCodeOpen && readyEmbedToken && embedPublishId ? (
          <DynamicEmbedCodeModal
            token={readyEmbedToken.token}
            publishId={embedPublishId}
            allowedOrigins={readyEmbedToken.allowedOrigins}
            onClose={() => setEmbedCodeOpen(false)}
          />
        ) : null}
        <section className="agent-home__progress">
          <p className="agent-home__rail-label">{uiText("Initial setup")}</p>
          <div className="agent-home__action-cards">
            {stateLoading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="agent-home__action-card agent-home__action-card--skeleton" />
                ))
              : actionCards.map((card) => {
                  const panelKey = isDay0PanelKey(card.key) ? card.key : null;
                  const open = panelKey != null && panel === panelKey;
                  return (
                    <div
                      key={card.key}
                      className={`agent-home__accordion${open ? ' agent-home__accordion--open' : ''}`}
                    >
                      <button
                        type="button"
                        className={`agent-home__action-card${card.isComplete ? ' agent-home__action-card--done' : ''}${open ? ' agent-home__action-card--open' : ''}`}
                        aria-expanded={panelKey ? open : undefined}
                        onClick={() => handleCardClick(card.key)}
                        title={card.key === 'ingest' ? uiText("Click to ask") : open ? uiText("Close") : uiText("Click to open")}
                      >
                        <span className="agent-home__action-icon">{card.icon}</span>
                        <span className="agent-home__action-text">
                          <span className="agent-home__action-title">{card.title}</span>
                          <span className="agent-home__action-desc">{card.description}</span>
                        </span>
                        {(card.isComplete || panelKey) && (
                          <span className="agent-home__action-trailing">
                            {card.isComplete && (
                              <span className="agent-home__action-badge">{uiText("Complete")}</span>
                            )}
                            {panelKey && (
                              <span
                                className={`agent-home__action-chevron${open ? ' agent-home__action-chevron--open' : ''}`}
                                aria-hidden
                              />
                            )}
                          </span>
                        )}
                      </button>
                      {open && panelKey === 'embedSecurity' && (
                        <div className="agent-home__accordion-body">
                          <EmbedSecurityPanel
                            origins={readyEmbedToken?.allowedOrigins ?? []}
                            siteUrl={embedSiteDraft}
                            saving={panelSaving}
                            removing={embedOriginRemoving}
                            onSiteUrlChange={setEmbedSiteDraft}
                            onSubmit={(siteUrl) => { void handleRegisterEmbedSite(siteUrl); }}
                            onRemove={(origin) => { void handleRemoveEmbedSite(origin); }}
                            onClose={() => setPanel(null)}
                          />
                        </div>
                      )}
                      {open && panelKey === 'appearance' && (
                        <div className="agent-home__accordion-body">
                          <AppearancePanel
                            workspaceId={workspaceId}
                            draft={appearanceDraft}
                            saving={panelSaving}
                            dirty={!appearanceDraftsEqual(appearanceDraft, appearanceBaseline)}
                            onChange={setAppearanceDraft}
                            onSave={() => { void handleSaveAppearance(); }}
                            onCancel={() => setAppearanceDraft({ ...appearanceBaseline })}
                            onClose={() => setPanel(null)}
                          />
                        </div>
                      )}
                      {open && panelKey === 'contact' && (
                        <div className="agent-home__accordion-body">
                          <ContactPathPanel
                            workspaceId={workspaceId}
                            seed={contactSeed}
                            onSaved={handleContactSaved}
                            onClose={() => setPanel(null)}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
          </div>
        </section>
      </aside>
    </div>
  );
};

export default AgentHomePage;
